import { PHASES } from '../constants';
import { scoreFor } from '../scoring';

// The two moves, and the end of the game. Everything is a pure function of
// (state, ...) -> newState; nothing mutates its input.

const clone = (state) => ({
  ...state,
  meta: { ...state.meta, seq: (state.meta.seq || 0) + 1 },
  players: state.players.map((p) => ({ ...p, cards: [...p.cards] })),
  deck: [...state.deck],
  log: [...state.log],
});

export const activePlayer = (state) => state.players[state.meta.activePlayerIndex];

/**
 * A player with no counters cannot decline - "When you don't have any
 * counters left, you must take the card." (p3, Game Play 4)
 *
 * This is the only forced move in the game, and it's the mechanism that
 * eventually ends every standoff: counters are finite, so somebody always
 * has to take.
 */
export const mustTake = (state) => (activePlayer(state)?.counters ?? 0) <= 0;

export const canDecline = (state) => state.meta.phase === PHASES.PLAYING && !mustTake(state);

export const canTake = (state) => state.meta.phase === PHASES.PLAYING && state.currentCard !== null;

/**
 * Take the face-up card and every counter sitting on it.
 *
 * "Take the card and put it face-up in front of you (this means that you
 * collect the points on the card)" (p2, 1a-i), and the counters on it come
 * with it - the worked example on p3 has Eli take the 11 and add "the three
 * counters on it to the counters in his hand."
 *
 * Then: "Every time you take a card, re-start play by flipping over the top
 * card from the stack." (p2, Game Play 3) - the SAME player continues. This
 * is the rule most easily got wrong, because every other card game passes
 * the turn after you act. Here, taking a card means you keep going.
 */
export const takeCard = (state) => {
  if (state.meta.phase !== PHASES.PLAYING) return state;
  if (state.currentCard === null || state.currentCard === undefined) return state;

  const next = clone(state);
  const player = next.players[next.meta.activePlayerIndex];
  const card = next.currentCard;
  const gained = next.countersOnCard;

  player.cards = [...player.cards, card].sort((a, b) => a - b);
  player.counters += gained;

  next.log.push({
    kind: 'take', player: player.name, playerId: player.id, card, counters: gained,
  });

  if (next.deck.length === 0) {
    // That was the last card. The stack is gone and there is nothing left to
    // collect, which is exactly the rulebook's end condition (p3, Winning the
    // Game). No further flip, no turn pass.
    next.currentCard = null;
    next.countersOnCard = 0;

    // The round's scores are banked now, while the cards that earned them
    // are still on the table. In a multi-round match (a house rule, see
    // constants.js) the next deal clears the table, and the running total
    // is the sum of these.
    const round = next.meta.round ?? 1;
    const scores = {};
    next.players.forEach((p) => { scores[p.id] = scoreFor(p); });
    next.roundResults = [...(next.roundResults || []), { round, scores }];

    if (round < (next.meta.rounds ?? 1)) {
      next.meta.phase = PHASES.ROUND_OVER;
      next.log.push({ kind: 'roundEnd', round });
    } else {
      next.meta.phase = PHASES.FINISHED;
      next.log.push({ kind: 'gameEnd' });
    }
    return next;
  }

  next.currentCard = next.deck.pop();
  next.countersOnCard = 0;
  next.meta.turn += 1;
  next.log.push({ kind: 'flip', card: next.currentCard, player: player.name });

  return next;
};

/**
 * Say "No Thanks!" - pay one counter onto the card and pass to the left.
 *
 * "Say 'No Thanks!' and put a counter from your supply of counters onto the
 * card so you don't have to take it." (p2, 1a-ii)
 *
 * Illegal with an empty supply; returns state unchanged rather than throwing,
 * so a mis-wired UI button degrades into a no-op instead of a crash. The
 * legality check the UI should be using is canDecline().
 */
export const declineCard = (state) => {
  if (!canDecline(state)) return state;

  const next = clone(state);
  const player = next.players[next.meta.activePlayerIndex];

  player.counters -= 1;
  next.countersOnCard += 1;
  next.meta.activePlayerIndex = (next.meta.activePlayerIndex + 1) % next.players.length;
  next.meta.turn += 1;

  next.log.push({
    kind: 'decline',
    player: player.name,
    playerId: player.id,
    card: next.currentCard,
    countersOnCard: next.countersOnCard,
  });

  return next;
};
